import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  LayoutDashboard,
  Settings,
  Target,
  BookOpen,
  Users,
  FolderKanban,
  BarChart3,
  Search,
  Rocket,
  Activity,
  CheckCircle,
  CheckSquare,
  Settings2,
  FlaskConical,
  Hammer,
  Code2,
  LogOut,
  // Success & Support icons
  Building2,
  Ticket,
  Inbox,
  Library,
  Compass,
  // Sales & Marketing icons
  FileText,
  Calendar,
  Share2,
  // Operations icons
  Briefcase,
  Receipt,
  UserCog,
  ClipboardList,
  Scale,
  Landmark,
  FileSpreadsheet,
  Trash2,
  // Collapse/expand icons
  PanelLeftClose,
  PanelLeftOpen,
  // Archie pane icons
  Play,
  Database,
  Shield,
  Gauge,
  Zap,
  Radio,
  Bot,
  Clock,
  FileSearch,
  Video,
  UserCheck,
  // Additional Archie icons
  Wrench,
  Sparkles,
  Globe,
  Layers,
  Network,
  LayoutTemplate,
  Linkedin,
  Key,
  TrendingUp,
  Hash,
} from 'lucide-react';
import { PaneSwitcher } from './PaneSwitcher';
import { ProjectSelector } from './ProjectSelector';
import { usePane, PANES, type PaneType } from '../../shared/contexts/PaneContext';
import { PANE_ICONS } from '../../shared/pane-icons';
import { PANE_NAV_SECTIONS, type NavSectionData } from '../../shared/route-registry';
import { useProject } from '../../contexts/ProjectContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../contexts/AuthContext';
import { GlobalSearchDialog } from '../search/GlobalSearchDialog';
import { NotificationBell } from '../notifications/index.js';
import { SidebarSprintPicker } from './SidebarSprintPicker';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  children?: NavItem[];
  /** If true, this path should be project-scoped (wrapped with /:prefix/{shortId}/) */
  projectScoped?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

// ── Icon map: nav path → icon (single place to maintain) ─────────────────
const NAV_ICONS: Record<string, React.ReactNode> = {
  // Product
  '/': <LayoutDashboard size={18} />,
  'documents': <BookOpen size={18} />,
  'trash': <Trash2 size={18} />,
  'boards': <FolderKanban size={18} />,
  'sprints': <Rocket size={18} />,
  'build-config': <Settings2 size={18} />,
  'queue': <ClipboardList size={18} />,
  'testing': <FlaskConical size={18} />,
  'environment': <Settings2 size={18} />,
  '/api/improvement/docs': <FileText size={18} />,
  // Success
  'dashboard': <LayoutDashboard size={18} />,
  'customers': <Building2 size={18} />,
  'users': <UserCheck size={18} />,
  'tickets': <Ticket size={18} />,
  'inbox': <Inbox size={18} />,
  'knowledge-base': <BookOpen size={18} />,
  'guides': <Compass size={18} />,
  'analytics': <BarChart3 size={18} />,
  // Sales
  'meetings': <Video size={18} />,
  'contacts': <Users size={18} />,
  'companies': <Building2 size={18} />,
  'deals': <Target size={18} />,
  'pipeline': <BarChart3 size={18} />,
  'forms': <FileText size={18} />,
  'content': <FileText size={18} />,
  'linkedin': <Linkedin size={18} />,
  'blog': <BookOpen size={18} />,
  'templates': <LayoutTemplate size={18} />,
  'brand-voice': <Sparkles size={18} />,
  'distribution': <Share2 size={18} />,
  'contributors': <Users size={18} />,
  // Ops
  'budgets': <Landmark size={18} />,
  'invoices': <Receipt size={18} />,
  'reports': <FileSpreadsheet size={18} />,
  'team': <Users size={18} />,
  'roles': <UserCog size={18} />,
  'onboarding': <ClipboardList size={18} />,
  'policies': <Scale size={18} />,
  'assets': <Briefcase size={18} />,
  // Archie
  'pipelines': <Play size={18} />,
  'models': <Database size={18} />,
  'datasets': <BarChart3 size={18} />,
  'quality-gates': <Shield size={18} />,
  'feedback': <Gauge size={18} />,
  'corrections': <CheckCircle size={18} />,
  'signals': <Zap size={18} />,
  'sessions': <Radio size={18} />,
  'fleet': <Bot size={18} />,
  'catalog': <Library size={18} />,
  'playbooks': <BookOpen size={18} />,
  'triggers': <Zap size={18} />,
  'audit': <FileSearch size={18} />,
  'regulatory-feeds': <Radio size={18} />,
  'scheduler': <Clock size={18} />,
  // Archie extras
  'skills': <Sparkles size={18} />,
  'logs': <FileText size={18} />,
  'firm-knowledge': <BookOpen size={18} />,
  'clients': <Users size={18} />,
  'demo': <Globe size={18} />,
  'staging': <FlaskConical size={18} />,
  'corpus-health': <Layers size={18} />,
  'design-studio': <Layers size={18} />,
  'playground': <LayoutTemplate size={18} />,
  'coordination': <Network size={18} />,
  'martha-mind': <Wrench size={18} />,
  'anonymiser': <Shield size={18} />,
  'sop-generator': <FileText size={18} />,
  'presence': <Radio size={18} />,
  // Connect (Archie pane)
  'connect/orgs': <Building2 size={18} />,
  'connect/events': <Activity size={18} />,
  'connect/keys': <Key size={18} />,
  'connect/usage': <TrendingUp size={18} />,
  'connect/endpoints': <Hash size={18} />,
  // Shared keys used across panes
  'calendar': <Calendar size={18} />,
  'my-calendar': <Calendar size={18} />,
  'costs': <LayoutDashboard size={18} />,
  'topics': <Wrench size={18} />,
  'media': <FileText size={18} />,
  'cms-settings': <Settings2 size={18} />,
};

// Per-pane icon overrides where the same path code needs a different icon
const PANE_ICON_OVERRIDES: Partial<Record<PaneType, Record<string, React.ReactNode>>> = {
  product: {
    'monitor': <Hammer size={18} />,
  },
  sales: {
    'calendar': <Calendar size={18} />,
    'landing-pages': <Globe size={18} />,
    'website-pages': <Globe size={18} />,
  },
  archie: {
    'evals': <FlaskConical size={18} />,
    'fleet': <Network size={18} />,
    'skills': <Sparkles size={18} />,
  },
};

/** Hydrate registry nav data with icons for a given pane */
function hydrateNavSections(pane: PaneType): NavSection[] {
  const overrides = PANE_ICON_OVERRIDES[pane];
  return PANE_NAV_SECTIONS[pane].map(section => ({
    title: section.title,
    items: section.items.map(item => ({
      path: item.path,
      label: item.label,
      projectScoped: item.projectScoped,
      icon: overrides?.[item.path] ?? NAV_ICONS[item.path] ?? <LayoutDashboard size={18} />,
    })),
  }));
}

// Precompute hydrated nav sections for each pane
const paneNavigation: Record<PaneType, NavSection[]> = {
  product: hydrateNavSections('product'),
  success: hydrateNavSections('success'),
  sales: hydrateNavSections('sales'),
  ops: hydrateNavSections('ops'),
  archie: hydrateNavSections('archie'),
  documentation: hydrateNavSections('documentation'),
};

/** Inline workspace switcher — tiny dropdown under logo */
function WorkspaceSwitcher() {
  const { currentWorkspace, workspaces, wsSlug } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Only show workspace switcher in the Product pane (/w/:slug/product/ or legacy /pr/)
  const isDevPane = /\/w\/[^/]+\/(product|pr)(\/|$)/.test(location.pathname);

  if (!currentWorkspace || workspaces.length <= 1 || !isDevPane) {
    return (
      <span className="block mt-1 text-[10px] text-subtle truncate">
        {currentWorkspace?.name}
      </span>
    );
  }

  return (
    <div ref={ref} className="relative mt-1">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-[10px] text-subtle hover:text-coral-500 truncate transition-colors duration-200"
      >
        <span className="truncate max-w-[140px]">{currentWorkspace.name}</span>
        <ChevronDown size={10} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 z-50 min-w-[160px] bg-white rounded-lg shadow-lg border border-line/80 py-1 animate-fade-in"
               style={{ animationDuration: '100ms' }}>
            {workspaces.map(ws => (
              <button
                key={ws.id}
                onClick={() => {
                  if (ws.id !== currentWorkspace.id) {
                    // Navigate to the new workspace, preserving the current pane prefix
                    const newSlug = ws.slug || 'archie';
                    const wsMatch = location.pathname.match(/^\/w\/[^/]+\/(.+)/);
                    const rest = wsMatch?.[1] || '';
                    navigate(`/w/${newSlug}/${rest}`);
                    setOpen(false);
                    return;
                  }
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-[11px] transition-colors
                  ${ws.id === currentWorkspace.id
                    ? 'bg-coral-50 text-coral-700 font-medium'
                    : 'text-muted hover:bg-surface-muted'}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${ws.id === currentWorkspace.id ? 'bg-coral-500' : 'bg-neutral-300'}`} />
                <span className="truncate">{ws.name}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** User avatar + dropdown at sidebar footer */
function UserMenu() {
  const { user, logout } = useAuth();
  const { wsSlug } = useWorkspace();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (!user) return null;

  const initials = user.name
    ? user.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : user.email[0].toUpperCase();

  const settingsPath = wsSlug ? `/w/${wsSlug}/settings` : '/settings';
  const membersPath = wsSlug ? `/w/${wsSlug}/settings/members` : '/settings/members';
  const devPath = wsSlug ? `/w/${wsSlug}/archie/development` : '/archie/development';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left hover:bg-surface-muted transition-all duration-200"
      >
        <div className="w-7 h-7 rounded-full bg-coral-100 text-coral-600 flex items-center justify-center text-xs font-semibold flex-shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium text-text truncate">{user.name || user.email}</p>
        </div>
        <ChevronDown size={14} className={`text-icon-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-2 bottom-full mb-1 z-50 w-56 bg-white rounded-lg shadow-lg border border-line/80 py-1 animate-fade-in"
               style={{ animationDuration: '100ms' }}>
            <div className="px-3 py-2 border-b border-line">
              <p className="text-xs text-subtle truncate">{user.email}</p>
            </div>
            <Link
              to={settingsPath}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-[12px] text-muted hover:bg-surface-muted hover:text-text transition"
            >
              <Settings size={14} className="text-icon-muted" />
              Settings
            </Link>
            <Link
              to={membersPath}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-[12px] text-muted hover:bg-surface-muted hover:text-text transition"
            >
              <Users size={14} className="text-icon-muted" />
              Workspace Members
            </Link>
            <Link
              to={devPath}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-[12px] text-muted hover:bg-surface-muted hover:text-text transition"
            >
              <Code2 size={14} className="text-icon-muted" />
              Development
            </Link>
            <div className="border-t border-line mt-1 pt-1">
              <button
                onClick={() => { void logout(); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-red-600 hover:bg-red-50 transition text-left"
              >
                <LogOut size={14} />
                Sign out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { activePane, paneInfo, setActivePane } = usePane();
  const { shortId, buildProjectUrl } = useProject();
  const { currentWorkspace, wsSlug: ctxWsSlug } = useWorkspace();
  // Fall back to URL-derived slug, then default workspace
  const wsSlug = ctxWsSlug ?? location.pathname.match(/^\/w\/([^/]+)/)?.[1] ?? 'archie';
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set(['do']));
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Collapsed state — persisted in localStorage
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('martha-sidebar-collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggleCollapsed = useCallback(() => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('martha-sidebar-collapsed', String(next)); } catch {}
      return next;
    });
  }, []);

  // Cmd/Ctrl+K shortcut (search)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(prev => !prev);
      }
      // Cmd+B to toggle sidebar collapse
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;
        e.preventDefault();
        toggleCollapsed();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleCollapsed]);

  const navSections = paneNavigation[activePane];

  // Extract shortId from current URL as fallback
  const urlShortId = useMemo(() => {
    if (shortId) return shortId;
    const prefix = PANES[activePane]?.urlPrefix;
    if (!prefix) return null;
    // Match both /w/:ws/:prefix/:shortId and legacy /:prefix/:shortId
    const match = location.pathname.match(new RegExp(`(?:^/w/[^/]+)?/${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/([a-z0-9]{7})(?:/|$)`));
    return match?.[1] ?? null;
  }, [shortId, activePane, location.pathname]);

  // ── URL builder: all paths go through /w/:wsSlug/... ──────────────────
  const resolvePath = (item: NavItem): string => {
    if (!wsSlug) {
      // Fallback for loading state
      return item.path.startsWith('/') ? item.path : `/${item.path}`;
    }

    const prefix = PANES[activePane].urlPrefix;

    if (item.projectScoped && urlShortId) {
      // Project-scoped: /w/:ws/:prefix/:shortId/:path
      return `/w/${wsSlug}/${prefix}/${urlShortId}/${item.path}`;
    }

    if (item.path === '/') {
      // Workspace overview
      return `/w/${wsSlug}`;
    }

    if (item.path.startsWith('/')) {
      // Absolute path (like /api/...) — leave as-is
      return item.path;
    }

    // Workspace-scoped: /w/:ws/:prefix/:path
    return `/w/${wsSlug}/${prefix}/${item.path}`;
  };

  const isActive = (path: string, item?: NavItem) => {
    const checkPath = item ? resolvePath(item) : path;
    if (checkPath === `/w/${wsSlug}` || checkPath === '/') {
      return location.pathname === checkPath || location.pathname === `/w/${wsSlug}`;
    }
    return location.pathname === checkPath || location.pathname.startsWith(checkPath + '/');
  };

  const toggleExpanded = (path: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  // Handle pane selection (for collapsed mode vertical pane tabs)
  const handlePaneSelect = useCallback((paneId: PaneType) => {
    setActivePane(paneId);
    const prefix = PANES[paneId].urlPrefix;
    if (wsSlug) {
      if (urlShortId && paneId === 'product') {
        navigate(`/w/${wsSlug}/${prefix}/${urlShortId}`);
      } else {
        navigate(`/w/${wsSlug}/${prefix}`);
      }
    } else {
      navigate(PANES[paneId].defaultPath);
    }
  }, [setActivePane, navigate, urlShortId, wsSlug]);

  // ─── Collapsed nav item renderer ─────────────────────────────────────
  const renderCollapsedNavItem = (item: NavItem) => {
    const active = isActive(item.path, item);
    const resolvedPath = resolvePath(item);

    return (
      <Link
        key={item.path}
        to={resolvedPath}
        title={item.label}
        className={`
          flex items-center justify-center w-10 h-10 mx-auto rounded-xl
          transition-all duration-200 group
          ${active
            ? 'bg-gradient-to-br from-coral-500/12 to-coral-400/6 text-coral-500 shadow-sm ring-1 ring-coral-500/10'
            : 'text-icon-muted hover:bg-surface-muted hover:text-icon'
          }
        `}
      >
        <span className={`transition-transform duration-200 ${active ? 'scale-110' : 'group-hover:scale-105'}`}>
          {item.icon}
        </span>
      </Link>
    );
  };

  // ─── Expanded nav item renderer ───────────────────────────────────────
  const renderNavItem = (item: NavItem, depth = 0, index = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.path);
    const active = isActive(item.path, item);
    const resolvedPath = resolvePath(item);
    const hasActiveChild = hasChildren && item.children!.some(child => isActive(child.path, child));

    return (
      <div
        key={item.path}
        className="animate-fade-in"
        style={{ animationDelay: `${index * 30}ms` }}
      >
        <div className="flex items-center group">
          {hasChildren ? (
            <button
              onClick={() => toggleExpanded(item.path)}
              className={`
                flex-1 flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium
                transition-all duration-200 ease-out text-left
                ${depth > 0 ? 'ml-7' : ''}
                ${hasActiveChild
                  ? 'text-coral-600'
                  : 'text-muted hover:bg-surface-muted hover:text-text'
                }
              `}
            >
              <span className={`
                transition-all duration-200
                ${hasActiveChild ? 'text-coral-500 scale-110' : 'text-icon-muted group-hover:text-icon group-hover:scale-105'}
              `}>
                {item.icon}
              </span>
              <span className="tracking-tight">{item.label}</span>
              <span className={`ml-auto transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}>
                <ChevronDown size={14} className="text-icon-muted" />
              </span>
            </button>
          ) : (
            <Link
              to={resolvedPath}
              className={`
                flex-1 flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium
                transition-all duration-200 ease-out
                ${depth > 0 ? 'ml-7' : ''}
                ${active
                  ? 'bg-gradient-to-r from-coral-500/10 to-coral-400/5 text-coral-600 shadow-sm'
                  : 'text-muted hover:bg-surface-muted hover:text-text'
                }
              `}
            >
              <span className={`
                transition-all duration-200
                ${active ? 'text-coral-500 scale-110' : 'text-icon-muted group-hover:text-icon group-hover:scale-105'}
              `}>
                {item.icon}
              </span>
              <span className="tracking-tight">{item.label}</span>
              {active && (
                <span className="ml-auto w-1.5 h-1.5 rounded-full bg-coral-500 animate-pulse" />
              )}
            </Link>
          )}
        </div>
        {hasChildren && (
          <div className={`
            overflow-hidden transition-all duration-300 ease-out
            ${isExpanded ? 'max-h-96 opacity-100 mt-1' : 'max-h-0 opacity-0'}
          `}>
            <div className="relative pl-3">
              <div className="absolute left-6 top-0 bottom-2 w-px bg-gradient-to-b from-coral-200 to-transparent" />
              {item.children!.map((child, idx) => renderNavItem(child, depth + 1, idx))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Build settings path
  const settingsPath = wsSlug ? `/w/${wsSlug}/settings` : '/settings';

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <aside
      className={`
        ${collapsed ? 'w-[68px]' : 'w-[260px]'}
        bg-gradient-to-b from-white via-white to-neutral-50
        border-r border-line/80 flex flex-col h-full relative overflow-hidden z-10
        transition-[width] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
      `}
    >
      {/* Subtle texture overlay */}
      <div className="absolute inset-0 opacity-[0.015] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* ── Collapsed Layout ────────────────────────────────────────────── */}
      {collapsed ? (
        <>
          {/* Logo + expand toggle */}
          <div className="flex flex-col items-center pt-5 pb-3 gap-2">
            <Link to={wsSlug ? `/w/${wsSlug}/${PANES[activePane].urlPrefix}` : '/'} className="group" title="martha.dev">
              <span className="text-[22px] font-bold tracking-tight bg-gradient-to-br from-coral-500 via-coral-400 to-coral-500 bg-clip-text text-transparent
                group-hover:from-coral-600 group-hover:via-coral-500 group-hover:to-coral-600 transition-all duration-300">
                m
              </span>
            </Link>
            <button
              onClick={toggleCollapsed}
              title="Expand sidebar (⌘B)"
              className="p-1.5 text-icon-muted hover:text-icon hover:bg-surface-subtle rounded-lg transition-all duration-200"
            >
              <PanelLeftOpen size={14} />
            </button>
          </div>

          {/* Pane tabs — vertical icon stack */}
          <div className="flex flex-col items-center gap-0.5 px-2 pb-2">
            {(Object.keys(PANES) as PaneType[]).map(paneId => (
              <button
                key={paneId}
                onClick={() => handlePaneSelect(paneId)}
                title={PANES[paneId].name}
                className={`
                  w-10 h-10 flex items-center justify-center rounded-xl
                  transition-all duration-200
                  ${activePane === paneId
                    ? 'bg-coral-50 text-coral-500'
                    : 'text-icon-muted hover:bg-surface-muted hover:text-icon'
                  }
                `}
              >
                {PANE_ICONS[paneId]}
              </button>
            ))}
          </div>

          {/* Separator */}
          <div className="px-3 py-1.5">
            <div className="h-px bg-gradient-to-r from-transparent via-neutral-200/70 to-transparent" />
          </div>

          {/* Search icon */}
          <div className="flex justify-center py-1">
            <button
              onClick={() => setIsSearchOpen(true)}
              title="Search (⌘K)"
              className="w-10 h-10 flex items-center justify-center rounded-xl
                text-icon-muted hover:bg-surface-muted hover:text-icon
                transition-all duration-200"
            >
              <Search size={18} />
            </button>
          </div>

          {/* Tasks — top-level, cross-project */}
          <div className="flex justify-center py-0.5">
            <Link
              to={wsSlug ? `/w/${wsSlug}/tasks` : '/tasks'}
              title="Tasks"
              className={`
                w-10 h-10 flex items-center justify-center rounded-xl
                transition-all duration-200 group
                ${location.pathname.includes('/tasks')
                  ? 'bg-gradient-to-br from-coral-500/12 to-coral-400/6 text-coral-500 shadow-sm ring-1 ring-coral-500/10'
                  : 'text-icon-muted hover:bg-surface-muted hover:text-icon'
                }
              `}
            >
              <span className={`transition-transform duration-200 ${location.pathname.includes('/tasks') ? 'scale-110' : 'group-hover:scale-105'}`}>
                <CheckSquare size={18} />
              </span>
            </Link>
          </div>

          {/* Separator */}
          <div className="px-3 py-1.5">
            <div className="h-px bg-gradient-to-r from-transparent via-neutral-200/70 to-transparent" />
          </div>

          {/* Nav items — icons only, grouped with subtle separators */}
          <nav className="flex-1 overflow-y-auto py-1 kanban-scrollbar-subtle">
            <div className="flex flex-col items-center gap-0.5">
              {navSections.map((section, sectionIdx) => (
                <div key={section.title} className="w-full flex flex-col items-center gap-0.5">
                  {sectionIdx > 0 && (
                    <div className="w-5 h-px bg-surface-subtle/50 my-1.5" />
                  )}
                  {section.items.map(item => renderCollapsedNavItem(item))}
                </div>
              ))}
            </div>
          </nav>

          {/* Footer — Settings + status */}
          <div className="relative border-t border-line/80 bg-gradient-to-t from-neutral-50/80 to-transparent">
            <div className="flex flex-col items-center gap-2 py-3">
              <Link
                to={settingsPath}
                title="Settings"
                className={`
                  w-10 h-10 flex items-center justify-center rounded-xl
                  transition-all duration-200 group
                  ${isActive(settingsPath)
                    ? 'bg-gradient-to-br from-coral-500/12 to-coral-400/6 text-coral-500 shadow-sm ring-1 ring-coral-500/10'
                    : 'text-icon-muted hover:bg-surface-muted hover:text-icon'
                  }
                `}
              >
                <span className={`transition-transform duration-200 ${isActive(settingsPath) ? 'scale-110' : 'group-hover:scale-105'}`}>
                  <Settings size={18} />
                </span>
              </Link>
              <span className="relative flex h-2 w-2" title={`${__GIT_HASH__.slice(-2)} @ ${__BUILD_TIME__}`}>
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
            </div>
          </div>
        </>
      ) : (
        /* ── Expanded Layout ──────────────────────────────────────────── */
        <>
          {/* Logo/Brand */}
          <div className="relative p-6 pb-4">
            <div className="flex items-center justify-between">
              <Link to={wsSlug ? `/w/${wsSlug}/${PANES[activePane].urlPrefix}` : '/'} className="flex items-baseline gap-1 group">
                <span className="text-[28px] font-bold tracking-tight bg-gradient-to-r from-coral-500 via-coral-400 to-coral-500 bg-clip-text text-transparent
                  group-hover:from-coral-600 group-hover:via-coral-500 group-hover:to-coral-600 transition-all duration-300">
                  martha
                </span>
                <span className="text-sm font-medium text-subtle group-hover:text-coral-400 transition-colors duration-300">.dev</span>
              </Link>
              <button
                onClick={toggleCollapsed}
                title="Collapse sidebar (⌘B)"
                className="p-1.5 text-icon-muted hover:text-icon hover:bg-surface-subtle rounded-lg transition-all duration-200"
              >
                <PanelLeftClose size={15} />
              </button>
            </div>
            {currentWorkspace && (
              <WorkspaceSwitcher />
            )}
          </div>

          {/* Pane Switcher */}
          <div className="px-4 pb-3">
            <PaneSwitcher />
          </div>

          {/* Minimal Project Selector — only for product pane */}
          {activePane === 'product' && <ProjectSelector />}

          {/* Sprint picker — only for product pane, hidden on Monitor page which has its own */}
          {activePane === 'product' && !location.pathname.includes('/monitor') && <SidebarSprintPicker />}

          {/* Decorative separator */}
          <div className="px-4 py-2">
            <div className="h-px bg-gradient-to-r from-transparent via-neutral-200/70 to-transparent" />
          </div>

          {/* Global Search */}
          <div className="px-4 pb-2">
            <button
              onClick={() => setIsSearchOpen(true)}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px]
                text-muted bg-surface-muted border border-line/80
                hover:border-line-strong hover:text-text hover:bg-surface-subtle
                transition-all duration-150 group"
            >
              <Search size={15} className="text-icon-muted group-hover:text-icon transition-colors" />
              <span className="flex-1 text-left">Search...</span>
              <kbd className="text-[10px] font-mono text-subtle bg-white border border-line rounded px-1.5 py-0.5 shadow-sm">
                ⌘K
              </kbd>
            </button>
          </div>

          {/* Tasks — only for product pane */}
          {activePane === 'product' && (
          <div className="px-4 pb-2">
            <Link
              to={wsSlug ? `/w/${wsSlug}/tasks` : '/tasks'}
              className={`
                flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium
                transition-all duration-200 ease-out group
                ${location.pathname.includes('/tasks')
                  ? 'bg-gradient-to-r from-coral-500/10 to-coral-400/5 text-coral-600 shadow-sm'
                  : 'text-muted hover:bg-surface-muted hover:text-text'
                }
              `}
            >
              <span className={`
                transition-all duration-200
                ${location.pathname.includes('/tasks') ? 'text-coral-500 scale-110' : 'text-icon-muted group-hover:text-icon group-hover:scale-105'}
              `}>
                <CheckSquare size={18} />
              </span>
              <span className="tracking-tight">Tasks</span>
              {location.pathname.includes('/tasks') && (
                <span className="ml-auto w-1.5 h-1.5 rounded-full bg-coral-500 animate-pulse" />
              )}
            </Link>
          </div>
          )}

          {/* Navigation Sections */}
          <nav className="flex-1 overflow-y-auto px-4 py-2 space-y-5 kanban-scrollbar-subtle">
            {navSections.map((section, sectionIdx) => (
              <div
                key={section.title}
                className="animate-fade-in"
                style={{ animationDelay: `${sectionIdx * 50}ms` }}
              >
                <h3 className="px-3 mb-1.5 text-[10px] font-semibold text-subtle tracking-[0.15em] uppercase flex items-center gap-2">
                  <span className="w-4 h-px bg-gradient-to-r from-coral-300/50 to-transparent" />
                  {section.title}
                </h3>
                <div className="space-y-0.5">
                  {section.items.map((item, idx) => renderNavItem(item, 0, idx))}
                </div>
              </div>
            ))}
          </nav>

          {/* Footer with User Menu, Notifications and Status */}
          <div className="relative border-t border-line/80 bg-gradient-to-t from-neutral-50/80 to-transparent">
            <div className="p-4 pb-2 flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <UserMenu />
              </div>
              <NotificationBell className="flex-shrink-0" />
            </div>

            {/* Status bar */}
            <div className="px-4 pb-4">
              <div className="flex items-center justify-between">
                <SystemStatusBadge />
                <span className="text-[10px] font-mono text-subtle flex-shrink-0" title={`Commit: ${__GIT_HASH__} | Built: ${__BUILD_TIME__}`}>{__GIT_HASH__.slice(-2)} @ {__BUILD_TIME__}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Global search — used for all panes (CRM search backend not yet implemented) */}
      <GlobalSearchDialog isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </aside>
  );
};

function SystemStatusBadge() {
  const [overall, setOverall] = useState<'operational' | 'degraded' | 'partial_outage' | 'major_outage' | null>(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await fetch('/api/status/components');
        if (!res.ok) return;
        const data = await res.json() as { components: Array<{ status: string }> };
        const components = data.components;
        const status =
          components.some(c => c.status === 'major_outage') ? 'major_outage' :
          components.some(c => c.status === 'partial_outage') ? 'partial_outage' :
          components.some(c => c.status === 'degraded') ? 'degraded' :
          'operational';
        setOverall(status as typeof overall);
      } catch { /* non-critical */ }
    };
    fetchStatus();
    const id = setInterval(fetchStatus, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  if (overall === null) return null;

  const cfg = {
    operational:    { dot: 'bg-emerald-500', ping: 'bg-emerald-400', label: 'All systems operational' },
    degraded:       { dot: 'bg-yellow-500',  ping: 'bg-yellow-400',  label: 'Degraded performance' },
    partial_outage: { dot: 'bg-orange-500',  ping: 'bg-orange-400',  label: 'Partial outage' },
    major_outage:   { dot: 'bg-red-500',     ping: 'bg-red-400',     label: 'Major outage' },
  }[overall];

  return (
    <Link to="/status" className="flex items-center gap-2 group min-w-0">
      <span className="relative flex h-2 w-2 flex-shrink-0">
        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${cfg.ping} opacity-75`} />
        <span className={`relative inline-flex rounded-full h-2 w-2 ${cfg.dot}`} />
      </span>
      <span className="text-[10px] font-mono text-subtle group-hover:text-muted transition-colors truncate">
        {cfg.label}
      </span>
    </Link>
  );
}

export default Sidebar;
